import {
  Button,
  Card,
  Center,
  Dialog,
  Divider,
  Flex,
  Group,
  LoadingOverlay,
  Text,
  Badge
} from "@mantine/core";
import {
  IconDownload,
  IconMessage,
  IconPencil,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { useDrawerStore } from "../../../store/dashboard.store";
import AddPatient from "./AddPatient";
import { useEventStore } from "../../../store/events.store";
import { IEvent } from "../../../models/events";
import { useEffect, useState } from "react";
import EditPatient from "./EditPatient";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import SendSMS from "./SendSMS";
import fileDownload from "js-file-download";
import dayjs from "dayjs";
import EditEvent from "./EditEvent";

const ViewEvent = ({ event }: { event: IEvent }) => {
  const addPage = useDrawerStore((state) => state.addPage);
  const [opened, { toggle, close }] = useDisclosure(false);
  const [openedEvent, { toggle: toggleEvent, close: closeEvent }] =
    useDisclosure(false);
  // const [openedReminder, { toggle: toggleReminder, close: closeReminder }] = useDisclosure(false);
  const [deleting, setDeleting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [loadingFile, setLoadingFile] = useState(false);
  // const [loadingReminder, setLoadingReminder] = useState(false);
  const { deleteEvent, getPatients, patients, deletePatient } = useEventStore(
    (state) => state,
  );

  const { popPage } = useDrawerStore((state) => state);

  useEffect(() => {
    const fetchPatients = async () => {
      await getPatients(event.uid!);
    };
    fetchPatients();
  }, [event.uid, getPatients]);

  return (
    <div>
      <Group mb="sm">
        <Button
          color="blue"
          size="xs"
          leftSection={<IconPlus />}
          onClick={() => addPage(<AddPatient event={event} />, "Add Patient")}
        >
          Add Patient
        </Button>
        {/* <Button
                    color="blue"
                    size="xs"
                    leftSection={<IconMessage />}
                    onClick={async () => {
                        toggleReminder();
                    }}
                >Send Reminder</Button> */}
        <Button
          color="blue"
          size="xs"
          leftSection={<IconPencil />}
          onClick={() => {
            addPage(<EditEvent event={event} />, `Edit Event (Event Id: ${event.uid})`);
          }}
        >
          Edit Event
        </Button>
        <Button
          color="blue"
          size="xs"
          leftSection={<IconMessage />}
          onClick={() => {
            addPage(<SendSMS event={event} patients={patients} />, "Send SMS");
          }}
        >
          Send SMS Message
        </Button>
        <Button
          color="green"
          size="xs"
          loading={loadingFile}
          leftSection={<IconDownload />}
          onClick={async () => {
            setLoadingFile(true);
            try {
              const response = await fetch(
                `${import.meta.env.VITE_MAIN_API}/excel/${event.uid}/`,
              );

              if (!response.ok) {
                const errorMessage = await response.text();
                throw new Error(errorMessage || "Failed to download Excel file.");
              }

              const blob = await response.blob();
              fileDownload(
                blob,
                `${dayjs(event.date).format("YYYY-MM-DD")}-${event.type}.xlsx`,
              );
            } catch (error) {
              notifications.show({
                title: "Download failed",
                message:
                  error instanceof Error
                    ? error.message
                    : "Failed to download Excel file.",
                color: "red",
              });
            } finally {
              setLoadingFile(false);
            }
          }}
        >
          Download Excel
        </Button>
        <Button
          color="red"
          size="xs"
          leftSection={<IconTrash />}
          onClick={toggleEvent}
        >
          Delete Event
        </Button>
      </Group>
      <Divider />
      {patients?.map((patient) => (
        <Card m="sm" p="sm" key={patient.uid}>
          <h3>
            {patient.firstName} {patient.lastName}
          </h3>
          <Flex justify={"space-between"}>
            <b>Sign Up ID</b>
            <span>{patient.uid}</span>
          </Flex>
          <Flex justify={"space-between"}>
            <b>Questions Only</b>
            {
              patient.officeStaffOnly === "0" || patient.officeStaffOnly ?
              <Badge color="green">Yes</Badge>
              : <span>No</span>
            }
          </Flex>
          <Divider />
          <Flex justify={"space-between"}>
            <b>Is New Patient</b>
            {
              patient.isNewPatient === "yes" ?
              <Badge color="green">Yes</Badge>
              : <span>No</span>
            }
          </Flex>
          <Divider />
          <Flex justify={"space-between"}>
            <b>Date of Birth</b>
            <span>{dayjs(patient.dob).format("MM/DD/YYYY")}</span>
          </Flex>
          <Divider />
          <Flex justify={"space-between"}>
            <b>Phone</b>
            <span>{patient.phone}</span>
          </Flex>
          <Divider />
          {patient.email && (
            <>
              <Flex justify={"space-between"}>
                <b>Email</b>
                <span>{patient.email}</span>
              </Flex>
              <Divider />
            </>
          )}
          {patient.address && (
            <>
              <Flex justify={"space-between"}>
                <b>Address</b>
                <address>
                  {patient.address} {patient.city}, {patient.zipCode} FL{" "}
                </address>
              </Flex>
              <Divider />
            </>
          )}
          <br />
          <Flex justify={"end"}>
            <Group>
              <Button
                leftSection={<IconPencil />}
                color="blue"
                onClick={() => {
                  addPage(
                    <EditPatient event={event} patient={patient} />,
                    "Edit Patient",
                  );
                }}
              >
                Edit Patient
              </Button>
              <Button
                leftSection={<IconTrash />}
                color="red"
                onClick={async () => {
                  setDeletingId(patient.uid!);
                  toggle();
                }}
              >
                Delete Patient
              </Button>
            </Group>
          </Flex>
        </Card>
      ))}
      {patients.length === 0 && (
        <Center>
          <Text size="lg">No patients found</Text>
        </Center>
      )}
      <Dialog
        opened={opened}
        withCloseButton
        onClose={close}
        size="lg"
        radius="md"
        position={{ top: "40%", left: "40%" }}
      >
        <LoadingOverlay visible={deleting} />
        <Text size="sm" mb="xs" fw={500}>
          Are you sure you want to delete this patient?
        </Text>

        <Group align="flex-end">
          <Button color="blue" onClick={close}>
            No
          </Button>
          <Button
            color="red"
            onClick={async () => {
              setDeleting(true);
              await deletePatient(event.uid!, deletingId!);
              close();
              setDeleting(false);
            }}
          >
            Yes
          </Button>
        </Group>
      </Dialog>
      <Dialog
        opened={openedEvent}
        withCloseButton
        onClose={closeEvent}
        size="lg"
        radius="md"
        position={{ top: "40%", left: "40%" }}
      >
        <LoadingOverlay visible={deleting} />
        <Text size="sm" mb="xs" fw={500}>
          Are you sure you want to delete this event?
        </Text>

        <Group align="flex-end">
          <Button color="blue" onClick={closeEvent}>
            No
          </Button>
          <Button
            color="red"
            onClick={async () => {
              setDeleting(true);
              await deleteEvent(event.uid!);
              closeEvent();
              popPage();
              setDeleting(false);
            }}
          >
            Yes
          </Button>
        </Group>
      </Dialog>
      {/* <Dialog
                opened={openedReminder}
                withCloseButton
                onClose={closeReminder}
                size="lg"
                radius="md"
                position={{ top: '40%', left: '40%' }}
            >
                <LoadingOverlay visible={loadingReminder} />
                <Text size="sm" mb="xs" fw={500}>
                    Are you sure you want to send a reminder for tomorrow?
                </Text>

                <Group align="flex-end">
                    <Button
                        color="blue"
                        onClick={closeReminder}>
                        No
                    </Button>
                    <Button
                        color="red"
                        onClick={async () => {
                            setLoadingReminder(true);
                            await sendMessageReminder(event);
                            close();
                            setLoadingReminder(false);
                            closeReminder();
                        }}>
                        Yes
                    </Button>
                </Group>
            </Dialog> */}
    </div>
  );
};

export default ViewEvent;
